import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import Btn from "../../components/form/btn";

// Import images for user type selection
import player from "../../assets/images/player.png";
import parent from "../../assets/images/parent.png";
import talentScout from "../../assets/images/talent-scout.png";
import organization from "../../assets/images/organization.png";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

export default function CompleteProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user, updateUser } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // User Type Selection
  const [selectedUserType, setSelectedUserType] = useState(0);
  const userTypes = [
    { name: t("auth.player"), image: player, value: "player" },
    { name: t("auth.parent"), image: parent, value: "parent" },
    { name: t("auth.talentScout"), image: talentScout, value: "talent-scout" },
    { name: t("auth.organization"), image: organization, value: "organization" }
  ];

  useEffect(() => {
    updatePageTitle(t("auth.completeProfile") + " - " + t("auth.accountType"), t("auth.chooseAccountType"));
    
    if (!user) {
      navigate("/signin");
      return;
    }

    // If user already has account type, redirect to next step
    if (user.accountType && user.accountType.trim() !== '') {
      navigate("/complete-profile/phone");
      return;
    }
  }, [user, navigate, updatePageTitle, t]);

  // User Type Selection Functions
  const handlePreviousUserType = () => {
    setSelectedUserType(prev => prev > 0 ? prev - 1 : userTypes.length - 1);
  };

  const handleNextUserType = () => {
    setSelectedUserType(prev => prev < userTypes.length - 1 ? prev + 1 : 0);
  };

  const handleAccountTypeSubmit = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      const token = localStorage.getItem("token");
      const accountType = userTypes[selectedUserType].value;

      const response = await fetch(`${API_URL}/user/set-user-type`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ accountType })
      });

      if (!response.ok) {
        throw new Error(t("errors.failedToUpdate"));
      }

      // Update user data
      const updatedUser = { ...user, accountType };
      updateUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Navigate to next step
      navigate("/complete-profile/phone");

    } catch (error) {
      setErrors({ accountType: error.message });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Progress Indicator */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center bg-white rounded-full px-6 py-3 shadow-lg">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-[#F15213] rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
              <span className="text-gray-600 font-medium">{t("auth.accountType")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">2</div>
              <span className="text-gray-400 font-medium">{t("auth.phone")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">3</div>
              <span className="text-gray-400 font-medium">{t("auth.details")}</span>
            </div>
            <div className="mx-4 w-8 h-0.5 bg-gray-200"></div>
            <div className="flex items-center space-x-2 opacity-50">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-gray-400 text-sm font-bold">4</div>
              <span className="text-gray-400 font-medium">{t("auth.documents")}</span>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#F15213] mb-4">
              {t("auth.chooseAccountType")}
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t("auth.selectAccountTypeDescription")}
            </p>
          </div>

          {errors.accountType && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-6 py-4 rounded-lg mb-8">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm">{errors.accountType}</p>
                </div>
              </div>
            </div>
          )}

          {/* Account Type Carousel */}
          <div className="flex items-center justify-center mb-12">
            <button 
              type="button" 
              onClick={handlePreviousUserType} 
              className="p-4 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="w-8 h-8 text-gray-600">
                <path fill="currentColor" d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
              </svg>
            </button>
            
            <div className="mx-12 text-center">
              <div className="relative">
                <div className="w-80 h-80 rounded-3xl bg-orange-50 flex items-center justify-center mb-6 shadow-2xl transform transition-all duration-500 hover:scale-105">
                  <img 
                    src={userTypes[selectedUserType].image} 
                    alt={userTypes[selectedUserType].name} 
                    className="w-64 h-64 object-contain filter drop-shadow-lg" 
                  />
                </div>
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                  <div className="bg-white rounded-full px-6 py-2 shadow-lg border-2 border-[#F15213]">
                    <h3 className="text-2xl font-bold text-[#F15213]">{userTypes[selectedUserType].name}</h3>
                  </div>
                </div>
              </div>
            </div>

            <button 
              type="button" 
              onClick={handleNextUserType} 
              className="p-4 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="w-8 h-8 text-gray-600">
                <path fill="currentColor" d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z" />
              </svg>
            </button>
          </div>

          {/* Type Indicators */}
          <div className="flex justify-center space-x-3 mb-12">
            {userTypes.map((_, index) => (
              <button
                key={index}
                onClick={() => setSelectedUserType(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === selectedUserType 
                    ? 'bg-[#F15213] scale-125' 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <Btn
              onClick={handleAccountTypeSubmit}
              disabled={isLoading}
              className="px-12 py-4 text-xl font-semibold bg-[#F15213] hover:bg-orange-600 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
            >
              {isLoading ? t("common.processing") : t("common.next")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
